import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  TreeSelect,
  Typography,
} from 'antd';
import { CopyOutlined, DeleteOutlined, ClearOutlined, DownloadOutlined, EditOutlined, FilterOutlined, UploadOutlined } from '@ant-design/icons';
import { openNotification } from '../../../utils';
import { listReportNodesAll } from '../../report-nodes/services/report-nodeService';
import {
  bulkUpdateQuestions,
  bulkCloneQuestions,
  bulkDeleteQuestions,
  downloadQuestionImportTemplate,
  importQuestionsFromExcel,
  cloneQuestion,
  deleteQuestion,
} from '../../questions/services/questionService';
import type { Question, QuestionBulkCloneRequest, QuestionBulkUpdateRequest, QuestionImportResponse } from '../../questions/types/Question';
import QuestionActions from '../../questions/views/QuestionActions';
import QuestionEditorDrawer from '../../questions/views/QuestionEditorDrawer';
import ToolService from 'services/ToolService';
import AttributeService from 'services/AttributeService';
import { apiFetch } from 'services/apiClient';
import { listReportFormatQuestions } from '../services/report-formatService';
import type { ReportFormatQuestionListItem } from '../types/ReportFormat';

export type QuestionListFilterValues = {
  title?: string;
  description?: string;
  questionType?: string[];
  reportStructureId?: number | null;
  parentScope?: 'root' | 'child';
  parentQuestionId?: number | null;
  toolId?: number | null;
  controlAreas?: number[];
  col?: number | null;
  titleCol?: number | null;
  fixedRowCount?: number | null;
  digits?: number | null;
  tag?: string;
  functionTag?: string;
  functionText?: string;
  checkReminderText?: string;
  minValue?: string;
  maxValue?: string;
  bulkInputMode?: string;
  isVisible?: boolean;
  excludeFromInspectorForm?: boolean;
  isRequired?: boolean;
  isCritical?: boolean;
  isChecked?: boolean;
  isDistinctive?: boolean;
  allowMultipleAnswers?: boolean;
  showAllAnswersInReport?: boolean;
  countable?: boolean;
  isCurrentYear?: boolean;
  isCurrentDay?: boolean;
};

interface AllQuestionsProps {
  reportFormatId: number;
  reportAttributeId?: number;
}

const QUESTION_TYPES = [
  ['MULTIPLE', 'Çoktan Seçmeli'], ['FREETEXT', 'Serbest Metin'], ['NETWORK_TYPE', 'Şebeke Tipi'], ['NUMBER', 'Sayı'], ['DATE', 'Tarih'],
  ['TIME', 'Saat'], ['YEAR', 'Yıl'], ['IMAGE', 'Görsel'], ['FUNCTION', 'Fonksiyon'], ['TABLE', 'Tablo Sorusu'],
  ['TITLE', 'Ünvan'], ['PHONE', 'Telefon'], ['ADDRESS', 'Adres'], ['EMAIL', 'E-posta'],
  ['SGK_REGISTRATION_NUMBER', 'SGK Sicil Numarası'], ['CONTRACT_ID', 'İSG Katip Sözleşme ID'],
  ['FIRST_NAME', 'Ad'], ['LAST_NAME', 'Soyad'], ['PROFESSION', 'Meslek'], ['DIPLOMA_DATE', 'Diploma Tarihi'],
  ['DIPLOMA_NUMBER', 'Diploma Numarası'], ['REGISTRATION_NUMBER', 'Kalıcı Kayıt Numarası'],
  ['IDENTITY_NUMBER', 'TC Kimlik No'], ['REGISTRATION_DATE', 'Kalıcı Kayıt Tarihi'], ['WORK_EQUIPMENT', 'İş Ekipmanı'],
  ['REPORT_NUMBER', 'Rapor Numarası'], ['REPORT_DATE', 'Rapor Tarihi'],
  ['CONTROL_START_DATE', 'Kontrol Başlangıç Tarihi'], ['CONTROL_END_DATE', 'Kontrol Bitiş Tarihi'],
  ['NEXT_CONTROL_DATE', 'Sonraki Kontrol Tarihi'], ['STARTED_AT', 'Başlangıç Saati'], ['FINISHED_AT', 'Bitiş Saati'],
] as const;

const BULK_FIELD_OPTIONS = [
  ['title', 'Başlık'], ['description', 'Açıklama'], ['questionType', 'Soru Tipi'], ['node', 'Rapor Yapısı'],
  ['toolId', 'Bileşen'], ['attributeId', 'Parametre'], ['controlAreas', 'Parametre Tipleri - Özellikleri'],
  ['col', 'Sütun'], ['titleCol', 'Başlık Sütunu'], ['isVisible', 'Raporda Gözükmeli'],
  ['excludeFromInspectorForm', 'PKM Veri Formunda Gizle'], ['isRequired', 'Zorunlu'], ['isCritical', 'Kritik'],
  ['isChecked', 'Kontrol Et'], ['checkReminderText', 'Kontrol Hatırlatma Metni'], ['tag', 'Etiket'],
  ['functionTag', 'Fonksiyon Etiketi'], ['functionText', 'Fonksiyon Metni'], ['isDistinctive', 'Ayırt Edici Bilgi'],
  ['allowMultipleAnswers', 'Birden Fazla Cevap'], ['showAllAnswersInReport', 'Raporda Tüm Cevaplar'],
  ['countable', 'Sayılabilir'], ['bulkInputMode', 'Veri Giriş Kapsamı'], ['fixedRowCount', 'Sabit Satır Sayısı'],
  ['minValue', 'Minimum'], ['maxValue', 'Maksimum'], ['digits', 'Ondalık Basamak'],
  ['isCurrentYear', 'Bu Yıl'], ['isCurrentDay', 'Bugün'],
] as const;

const BOOLEAN_FILTERS = [
  ['isVisible', 'Raporda Gözükmeli'], ['excludeFromInspectorForm', 'PKM Veri Formunda Gizle'],
  ['isRequired', 'Zorunlu'], ['isCritical', 'Kritik'], ['isChecked', 'Kontrol Et'],
  ['isDistinctive', 'Ayırt Edici Bilgi'], ['allowMultipleAnswers', 'Birden Fazla Cevap'],
  ['showAllAnswersInReport', 'Raporda Tüm Cevaplar'], ['countable', 'Sayılabilir'],
  ['isCurrentYear', 'Bu Yıl'], ['isCurrentDay', 'Bugün'],
] as const;

const toId = (value: any): number | undefined => {
  const raw = typeof value === 'object' ? value?.id : value;
  const numberValue = Number(raw);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const optionItems = (payload: any): Array<{ value: number; label: string }> => {
  const result = new Map<number, string>();
  const visit = (items: any[]) => items.forEach((item) => {
    const id = Number(item?.id);
    if (Number.isFinite(id) && !item?.isGroup) result.set(id, String(item?.name ?? item?.description ?? `#${id}`));
    if (Array.isArray(item?.children)) visit(item.children);
  });
  visit(Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []);
  return Array.from(result, ([value, label]) => ({ value, label }));
};

const booleanOptions = [
  { value: true, label: 'Evet' },
  { value: false, label: 'Hayır' },
];

const isActiveFilterValue = (value: any): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null;
};

export const countActiveFilters = (values: QuestionListFilterValues): number => Object.values(values).filter(isActiveFilterValue).length;

const hasQueryValue = (value: unknown): value is string | number | boolean => {
  if (typeof value === 'string') return value.trim().length > 0;
  return typeof value === 'number' || typeof value === 'boolean';
};

/** Converts only values entered in the filter drawer into the light-list API contract. */
export const buildQuestionListQuery = (
  filters: QuestionListFilterValues,
  selectedNodeAndDescendantIds: number[],
): string => {
  const params = new URLSearchParams();
  const add = (key: string, value: unknown) => {
    if (hasQueryValue(value)) params.append(key, typeof value === 'string' ? value.trim() : String(value));
  };
  const addAll = (key: string, values: unknown) => {
    if (Array.isArray(values)) values.forEach((value) => add(key, value));
  };

  add('title.contains', filters.title);
  add('description.contains', filters.description);
  addAll('questionType.in', filters.questionType);
  add('parentQuestionId.equals', filters.parentQuestionId);
  if (filters.parentScope === 'root') add('parentQuestionId.specified', false);
  if (filters.parentScope === 'child') add('parentQuestionId.specified', true);
  add('toolId.equals', filters.toolId);
  addAll('controlAreasId.in', filters.controlAreas);
  (['col', 'titleCol', 'fixedRowCount', 'digits'] as const).forEach((field) => add(`${field}.equals`, filters[field]));
  (['tag', 'functionTag', 'functionText', 'checkReminderText', 'minValue', 'maxValue'] as const)
    .forEach((field) => add(`${field}.contains`, filters[field]));
  add('bulkInputMode.equals', filters.bulkInputMode);
  BOOLEAN_FILTERS.forEach(([field]) => add(`${field}.equals`, filters[field as keyof QuestionListFilterValues]));
  selectedNodeAndDescendantIds.forEach((id) => add('nodeId.in', id));
  return params.toString();
};

export default function AllQuestions({ reportFormatId, reportAttributeId }: AllQuestionsProps) {
  const [filterForm] = Form.useForm<QuestionListFilterValues>();
  const [bulkForm] = Form.useForm<Record<string, any>>();
  const [bulkCloneForm] = Form.useForm<{ controlAreaIds?: number[] }>();
  const [filters, setFilters] = React.useState<QuestionListFilterValues>({});
  const [nodes, setNodes] = React.useState<any[]>([]);
  const [nodePaths, setNodePaths] = React.useState<Map<number, string>>(new Map());
  const [questions, setQuestions] = React.useState<ReportFormatQuestionListItem[]>([]);
  const [showReportStructure, setShowReportStructure] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [bulkCloneOpen, setBulkCloneOpen] = React.useState(false);
  const [bulkSummaryOpen, setBulkSummaryOpen] = React.useState(false);
  const [pendingBulkPayload, setPendingBulkPayload] = React.useState<QuestionBulkUpdateRequest>();
  const [importOpen, setImportOpen] = React.useState(false);
  const [importFile, setImportFile] = React.useState<File>();
  const [importResult, setImportResult] = React.useState<QuestionImportResponse>();
  const [downloadingTemplate, setDownloadingTemplate] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [editQuestion, setEditQuestion] = React.useState<Question | undefined>();
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkDeleting, setBulkDeleting] = React.useState(false);
  const bulkDeletePending = React.useRef(false);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [selectedQuestionsById, setSelectedQuestionsById] = React.useState<Map<number, ReportFormatQuestionListItem>>(new Map());
  const [saving, setSaving] = React.useState(false);
  const [bulkCloning, setBulkCloning] = React.useState(false);
  const [toolOptions, setToolOptions] = React.useState<Array<{ value: number; label: string }>>([]);
  const [attributeOptions, setAttributeOptions] = React.useState<Array<{ value: number; label: string }>>([]);
  const [controlAreaOptions, setControlAreaOptions] = React.useState<Array<{ value: number; label: string }>>([]);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const optionsRequest = React.useRef<Promise<void> | null>(null);
  const optionsScope = React.useRef(0);

  React.useEffect(() => {
    optionsScope.current += 1;
    optionsRequest.current = null;
    setNodes([]);
    setNodePaths(new Map());
    setToolOptions([]);
    setAttributeOptions([]);
    setControlAreaOptions([]);
  }, [reportAttributeId, reportFormatId]);

  const childrenByParent = React.useMemo(() => {
    const map = new Map<number, any[]>();
    nodes.forEach((node) => {
      const parentId = toId(node?.parent);
      if (parentId !== undefined) map.set(parentId, [...(map.get(parentId) ?? []), node]);
    });
    return map;
  }, [nodes]);

  const treeData = React.useMemo(() => {
    const compare = (left: any, right: any) => Number(left?.sortOrder ?? 0) - Number(right?.sortOrder ?? 0)
      || String(left?.title ?? '').localeCompare(String(right?.title ?? ''), 'tr');
    const toTree = (node: any): any => ({
      key: node.id,
      value: node.id,
      title: node.title ?? `#${node.id}`,
      children: (childrenByParent.get(Number(node.id)) ?? []).sort(compare).map(toTree),
    });
    const ids = new Set(nodes.map((node) => Number(node.id)));
    return nodes.filter((node) => !ids.has(toId(node?.parent) ?? -1)).sort(compare).map(toTree);
  }, [childrenByParent, nodes]);

  const selectedNodeAndDescendantIds = React.useMemo(() => {
    const selectedNodeId = toId(filters.reportStructureId);
    if (selectedNodeId === undefined) return [] as number[];
    const ids: number[] = [];
    const visit = (id: number) => {
      ids.push(id);
      (childrenByParent.get(id) ?? []).forEach((node) => visit(Number(node.id)));
    };
    visit(selectedNodeId);
    return ids;
  }, [childrenByParent, filters.reportStructureId]);

  const buildQuery = React.useCallback(
    () => buildQuestionListQuery(filters, selectedNodeAndDescendantIds),
    [filters, selectedNodeAndDescendantIds],
  );

  const query = React.useMemo(buildQuery, [buildQuery]);

  const loadQuestions = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await listReportFormatQuestions(reportFormatId, query);
      setQuestions(Array.isArray(response) ? response : []);
    } catch (error) {
      setQuestions([]);
      const message = error instanceof Error && error.message.trim()
        ? error.message
        : 'Sorular yüklenemedi.';
      openNotification('error', 'Hata', message);
    } finally {
      setLoading(false);
    }
  }, [reportFormatId, query]);

  React.useEffect(() => { void loadQuestions(); }, [loadQuestions, refreshKey]);

  React.useEffect(() => {
    if (!selectedRowKeys.length) return;
    const selectedIds = new Set(selectedRowKeys.map(Number));
    setSelectedQuestionsById((current) => {
      const next = new Map(current);
      questions.forEach((question) => {
        const id = Number(question.id);
        if (selectedIds.has(id)) next.set(id, question);
      });
      return next;
    });
  }, [questions, selectedRowKeys]);

  const ensureOptionsLoaded = React.useCallback(async () => {
    if (optionsRequest.current) return optionsRequest.current;
    const scope = optionsScope.current;
    const request = (async () => {
      try {
        const [reportNodes, tools, attributes, controlAreas] = await Promise.all([
          listReportNodesAll(`reportFormatId.equals=${reportFormatId}`),
          ToolService.getAllTools(),
          AttributeService.getAll(),
          apiFetch(`/control-areas${reportAttributeId ? `?attributeId=${reportAttributeId}` : ''}`),
        ]);
        const loadedNodes = Array.isArray(reportNodes) ? reportNodes : [];
        if (scope !== optionsScope.current) return;
        setNodes(loadedNodes);
        setToolOptions(optionItems(tools));
        setAttributeOptions(optionItems(attributes));
        setControlAreaOptions(optionItems(controlAreas));
        const byId = new Map<number, any>(loadedNodes.map((node: any) => [Number(node.id), node]));
        const paths = new Map<number, string>();
        const getPath = (node: any): string => {
          const id = Number(node.id);
          if (paths.has(id)) return paths.get(id)!;
          const parent = byId.get(toId(node?.parent) ?? -1);
          const value = `${parent ? `${getPath(parent)} / ` : ''}${node?.title ?? `#${id}`}`;
          paths.set(id, value);
          return value;
        };
        loadedNodes.forEach(getPath);
        setNodePaths(paths);
      } catch {
        openNotification('error', 'Hata', 'Filtre seçenekleri yüklenemedi.');
        optionsRequest.current = null;
      }
    })();
    optionsRequest.current = request;
    return request;
  }, [reportAttributeId, reportFormatId]);

  const toggleReportStructure = React.useCallback(() => {
    setShowReportStructure((currentValue) => {
      if (!currentValue) void ensureOptionsLoaded();
      return !currentValue;
    });
  }, [ensureOptionsLoaded]);

  const handleReportStructureLabelKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleReportStructure();
    }
  }, [toggleReportStructure]);

  const applyFilters = () => {
    setFilters(filterForm.getFieldsValue());
    setFilterOpen(false);
  };

  const openFilterDrawer = () => {
    filterForm.setFieldsValue(filters);
    setFilterOpen(true);
    void ensureOptionsLoaded();
  };

  const resetFilters = () => {
    const activeFields = Object.entries(filterForm.getFieldsValue(true))
      .filter(([, value]) => isActiveFilterValue(value))
      .reduce<QuestionListFilterValues>((values, [name]) => ({ ...values, [name as keyof QuestionListFilterValues]: undefined }), {});
    filterForm.setFieldsValue(activeFields);
    setFilters({});
  };

  const prepareBulkUpdate = async () => {
    const values = await bulkForm.validateFields();
    const fields = Array.isArray(values.fields) ? values.fields : [];
    const addStandardAnswers = allSelectedQuestionsAreMultiple && values.addStandardAnswers === true;
    if (!selectedRowKeys.length) return;
    if (!fields.length && !addStandardAnswers) {
      bulkForm.setFields([{ name: 'fields', errors: ['En az bir ayar veya cevap işlemi seçiniz.'] }]);
      return;
    }
    if (addStandardAnswers && fields.includes('questionType') && values.questionType !== 'MULTIPLE') {
      bulkForm.setFields([{ name: 'questionType', errors: ['Standart cevap eklenirken soru tipi Çoktan Seçmeli olmalıdır.'] }]);
      return;
    }
    const payload: QuestionBulkUpdateRequest = {
      reportFormatId,
      questionIds: selectedRowKeys.map(Number),
      fields,
      addStandardAnswers,
      ...values,
      node: values.node === undefined || values.node === null ? undefined : Number(values.node),
      controlAreas: Array.isArray(values.controlAreas) ? values.controlAreas.map(Number) : undefined,
    };
    delete (payload as any).fields;
    payload.fields = fields;
    payload.addStandardAnswers = addStandardAnswers;
    setPendingBulkPayload(payload);
    setBulkSummaryOpen(true);
  };

  const submitBulkUpdate = async () => {
    if (!pendingBulkPayload) return;
    setSaving(true);
    try {
      const response = await bulkUpdateQuestions(pendingBulkPayload);
      const resultMessages: string[] = [];
      if (response.updatedCount > 0) resultMessages.push(`${response.updatedCount} soru güncellendi.`);
      if (response.standardAnswerCreatedQuestionCount > 0) {
        resultMessages.push(`${response.standardAnswerCreatedQuestionCount} soruya ${response.createdAnswerCount} standart cevap eklendi.`);
      }
      if (response.standardAnswerSkippedQuestionCount > 0) {
        resultMessages.push(`${response.standardAnswerSkippedQuestionCount} soruda standart cevap bulunduğu için işlem yapılmadı.`);
      }
      openNotification('success', 'Başarılı', resultMessages.join(' ') || 'Toplu işlem tamamlandı.');
      setBulkSummaryOpen(false);
      setBulkOpen(false);
      setPendingBulkPayload(undefined);
      bulkForm.resetFields();
      setSelectedRowKeys([]);
      setSelectedQuestionsById(new Map());
      setRefreshKey((value) => value + 1);
    } catch {
      openNotification('error', 'Hata', 'Toplu güncelleme tamamlanamadı.');
    } finally {
      setSaving(false);
    }
  };

  const downloadImportTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await downloadQuestionImportTemplate(reportFormatId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapor-formati-${reportFormatId}-soru-sablonu.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      openNotification('error', 'Hata', 'Excel şablonu indirilemedi.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const importExcel = async () => {
    if (!importFile) {
      openNotification('warning', 'Uyarı', 'İçe aktarılacak .xlsx dosyasını seçiniz.');
      return;
    }
    setImporting(true);
    try {
      const response = await importQuestionsFromExcel(reportFormatId, importFile);
      setImportResult(response);
      if (response.success) {
        openNotification('success', 'Başarılı', `${response.createdCount} soru içe aktarıldı.`);
        setRefreshKey((value) => value + 1);
      }
    } catch {
      openNotification('error', 'Hata', 'Excel dosyası içe aktarılamadı.');
    } finally {
      setImporting(false);
    }
  };

  const handleClone = async (id: number) => {
    try {
      await cloneQuestion(id);
      openNotification('success', 'Başarılı', 'Soru kopyalandı');
      setRefreshKey((value) => value + 1);
    } catch {
      openNotification('error', 'Hata', 'Soru kopyalanamadı');
    }
  };

  const closeBulkCloneDrawer = () => {
    if (bulkCloning) return;
    bulkCloneForm.resetFields();
    setBulkCloneOpen(false);
  };

  const submitBulkClone = async () => {
    const values = await bulkCloneForm.validateFields();
    const controlAreaIds = Array.isArray(values.controlAreaIds) ? values.controlAreaIds.map(Number) : [];
    if (!selectedRowKeys.length || !controlAreaIds.length || bulkCloning) return;
    setBulkCloning(true);
    try {
      const payload: QuestionBulkCloneRequest = {
        reportFormatId,
        questionIds: selectedRowKeys.map(Number),
        controlAreaIds,
      };
      const response = await bulkCloneQuestions(payload);
      openNotification('success', 'Başarılı', `${response.createdQuestionCount} soru ve ${response.createdAnswerCount} cevap seçeneği kopyalandı.`);
      bulkCloneForm.resetFields();
      setBulkCloneOpen(false);
      setSelectedRowKeys([]);
      setSelectedQuestionsById(new Map());
      setRefreshKey((value) => value + 1);
    } catch (error) {
      openNotification('error', 'Hata', error instanceof Error && error.message ? error.message : 'Toplu kopyalama tamamlanamadı.');
    } finally {
      setBulkCloning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (bulkDeletePending.current || !selectedRowKeys.length) return;
    bulkDeletePending.current = true;
    setBulkDeleting(true);
    try {
      const result = await bulkDeleteQuestions({ reportFormatId, questionIds: selectedRowKeys.map(Number) });
      setSelectedRowKeys([]);
      setSelectedQuestionsById(new Map());
      setBulkDeleteOpen(false);
      setRefreshKey(value => value + 1);
      openNotification('success', 'Başarılı', `${result.deletedQuestionCount} soru ve ${result.deletedAnswerCount} cevap seçeneği silindi.`);
    } catch (error) {
      openNotification('error', 'Hata', error instanceof Error ? error.message : 'Toplu silme işlemi başarısız.');
    } finally {
      bulkDeletePending.current = false;
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteQuestion(id);
      openNotification('success', 'Başarılı', 'Soru Silindi');
      setSelectedRowKeys((current) => current.filter((key) => Number(key) !== id));
      setSelectedQuestionsById((current) => {
        const next = new Map(current);
        next.delete(id);
        return next;
      });
      setRefreshKey((value) => value + 1);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Soru silme işlemi başarısız';
      openNotification('error', 'Hata', message);
    }
  };

  const selectedBulkFields = Form.useWatch('fields', bulkForm) ?? [];
  const watchedAddStandardAnswers = Form.useWatch('addStandardAnswers', bulkForm) === true;
  const activeFilterCount = countActiveFilters(filters);
  const allSelectedQuestionsAreMultiple = selectedRowKeys.length > 0
    && selectedRowKeys.every((key) => String(selectedQuestionsById.get(Number(key))?.questionType ?? '').toUpperCase() === 'MULTIPLE');
  const bulkCloneDisabledReason = React.useMemo(() => {
    if (!selectedRowKeys.length) return undefined;
    const selectedQuestions = selectedRowKeys.map((key) => selectedQuestionsById.get(Number(key)));
    if (selectedQuestions.some((question) => question?.parentQuestionId !== null && question?.parentQuestionId !== undefined)) {
      return 'Tablo alt soruları toplu kopyalanamaz.';
    }
    if (selectedQuestions.some((question) => String(question?.functionTag ?? '').trim().length > 0)) {
      return 'Fonksiyon etiketli sorular toplu kopyalanamaz.';
    }
    return undefined;
  }, [selectedQuestionsById, selectedRowKeys]);
  const addStandardAnswers = allSelectedQuestionsAreMultiple && watchedAddStandardAnswers;
  const hasBulkField = (field: string) => selectedBulkFields.includes(field);
  const typeLabel = (value: any) => QUESTION_TYPES.find(([key]) => key === value)?.[1] ?? String(value ?? '-');
  const optionLabel = (options: Array<{ value: any; label: string }>, value: any) =>
    options.find((option) => String(option.value) === String(value))?.label ?? String(value ?? 'Boş');
  const bulkValueLabel = (field: string, value: any): string => {
    if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';
    if (field === 'questionType') return typeLabel(value);
    if (field === 'bulkInputMode') return value === 'SHARED' ? 'Ortak Bilgi' : 'Kontrol Noktası Bazlı';
    if (field === 'node') return nodePaths.get(Number(value)) ?? String(value ?? 'Boş');
    if (field === 'toolId') return optionLabel(toolOptions, value);
    if (field === 'attributeId') return optionLabel(attributeOptions, value);
    if (field === 'controlAreas') {
      if (!Array.isArray(value) || !value.length) return 'Boş';
      return value.map((item) => optionLabel(controlAreaOptions, item)).join(', ');
    }
    if (value === undefined || value === null || value === '') return 'Boş';
    return String(value);
  };
  const handleSelectionChange = (keys: React.Key[], selectedRows: ReportFormatQuestionListItem[]) => {
    const selectedIds = new Set(keys.map(Number));
    setSelectedRowKeys(keys);
    setSelectedQuestionsById((current) => {
      const next = new Map(current);
      selectedRows.forEach((question) => next.set(Number(question.id), question));
      Array.from(next.keys()).forEach((id) => {
        if (!selectedIds.has(id)) next.delete(id);
      });
      return next;
    });
  };
  const columns: any[] = [
    ...(showReportStructure ? [{
      title: 'Rapor Yapısı',
      dataIndex: 'nodeId',
      width: 260,
      render: (nodeId: number | null) => nodePaths.get(Number(nodeId)) ?? '-',
    }] : []),
    { title: 'Soru Tipi', dataIndex: 'questionType', width: 150, render: (value: any, item: ReportFormatQuestionListItem) => <Space size={4}><Tag>{typeLabel(value)}</Tag>{item.parentQuestionId ? <Tag color='purple'>Tablo altı</Tag> : null}</Space> },
    { title: 'Başlık', dataIndex: 'title', width: 300, render: (value: ReportFormatQuestionListItem['title']) => value ?? '-' },
    {
      title: 'Parametre Tipleri - Özellikleri',
      dataIndex: 'controlAreas',
      width: 240,
      render: (controlAreas: ReportFormatQuestionListItem['controlAreas']) => Array.isArray(controlAreas) && controlAreas.length
        ? <Space wrap size={[4, 4]}>{controlAreas.map((controlArea: any, index: number) => <Tag color='blue' key={controlArea?.id ?? `${controlArea?.name}-${index}`}>{controlArea?.name ?? '-'}</Tag>)}</Space>
        : '-',
    },
    { title: 'Sütun', dataIndex: 'col', width: 130, render: (value: ReportFormatQuestionListItem['col']) => value ?? '-' },
    { title: 'Başlık Sütunu', dataIndex: 'titleCol', width: 150, render: (value: ReportFormatQuestionListItem['titleCol']) => value ?? '-' },
    { title: 'Raporda', dataIndex: 'isVisible', width: 90, render: (value: boolean) => value !== false ? 'Evet' : 'Hayır' },
    { title: 'Zorunlu', dataIndex: 'isRequired', width: 90, render: (value: boolean) => value === true ? 'Evet' : 'Hayır' },
    { title: 'Kritik', dataIndex: 'isCritical', width: 80, render: (value: boolean) => value === true ? 'Evet' : 'Hayır' },
    {
      title: 'İşlemler',
      key: 'action',
      fixed: 'right',
      width: 220,
      render: (_: any, item: ReportFormatQuestionListItem) => (
        <QuestionActions
          question={item as Question}
          onClone={handleClone}
          onEdit={setEditQuestion}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  const renderBooleanFilter = ([field, label]: readonly [string, string]) => (
    <Col xs={24} md={12} key={field}><Form.Item name={field} label={label}><Select allowClear options={booleanOptions} /></Form.Item></Col>
  );

  const renderBulkControl = (field: string) => {
    if (!hasBulkField(field)) return null;
    const textFields = ['title', 'description', 'checkReminderText', 'tag', 'functionTag', 'functionText', 'minValue', 'maxValue'];
    const numberFields = ['col', 'titleCol', 'fixedRowCount', 'digits'];
    const booleanFields: readonly string[] = [
      'isVisible', 'excludeFromInspectorForm', 'isRequired', 'isCritical', 'isChecked', 'isDistinctive',
      'allowMultipleAnswers', 'showAllAnswersInReport', 'countable', 'isCurrentYear', 'isCurrentDay',
    ];
    const label = BULK_FIELD_OPTIONS.find(([key]) => key === field)?.[1] ?? field;
    if (textFields.includes(field)) return <Form.Item key={field} name={field} label={label}><Input.TextArea autoSize={field === 'description' || field === 'functionText'} /></Form.Item>;
    if (numberFields.includes(field)) return <Form.Item key={field} name={field} label={label}><InputNumber min={field === 'col' || field === 'titleCol' ? 1 : 0} max={field === 'col' || field === 'titleCol' ? 24 : undefined} style={{ width: '100%' }} /></Form.Item>;
    if (booleanFields.includes(field)) return <Form.Item key={field} name={field} label={label} rules={[{ required: true }]}><Select options={booleanOptions} /></Form.Item>;
    if (field === 'questionType') return <Form.Item key={field} name={field} label={label} rules={[{ required: true }]}><Select options={QUESTION_TYPES.map(([value, text]) => ({ value, label: text }))} /></Form.Item>;
    if (field === 'bulkInputMode') return <Form.Item key={field} name={field} label={label} rules={[{ required: true }]}><Select options={[{ value: 'PER_REPORT', label: 'Kontrol Noktası Bazlı' }, { value: 'SHARED', label: 'Ortak Bilgi' }]} /></Form.Item>;
    if (field === 'node') return <Form.Item key={field} name={field} label={label} rules={[{ required: true }]}><Select showSearch optionFilterProp='label' options={nodes.filter((node) => ['STANDARD', 'PANEL_CONTROL', 'APPROVAL', 'CONTROL_CRITERIA_AND_TESTS'].includes(String(node.type))).map((node) => ({ value: node.id, label: nodePaths.get(Number(node.id)) ?? node.title }))} /></Form.Item>;
    if (field === 'toolId') return <Form.Item key={field} name={field} label={label}><Select allowClear showSearch optionFilterProp='label' options={toolOptions} /></Form.Item>;
    if (field === 'attributeId') return <Form.Item key={field} name={field} label={label}><Select allowClear showSearch optionFilterProp='label' options={attributeOptions} /></Form.Item>;
    if (field === 'controlAreas') return <Form.Item key={field} name={field} label={label}><Select mode='multiple' showSearch optionFilterProp='label' options={controlAreaOptions} /></Form.Item>;
    return null;
  };

  return (
    <Row>
      <Col span={24}>
        <Card
          className='br-30px'
          title={
            <Space wrap>
              <Typography.Title level={4} style={{ margin: 0 }}>Tüm Sorular</Typography.Title>
              <Typography.Text type='secondary'>({questions.length})</Typography.Text>
              <Space size={6}>
                <Switch size='small' aria-label='Rapor yapısı sütununu göster' checked={showReportStructure} onChange={toggleReportStructure} />
                <Typography.Text
                  type='secondary'
                  role='button'
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={toggleReportStructure}
                  onKeyDown={handleReportStructureLabelKeyDown}
                >
                  Rapor Yapısını Göster
                </Typography.Text>
              </Space>
            </Space>
          }
          extra={(
            <Space wrap>
              <Tooltip title='Excel’den İçe Aktar'>
                <Button
                  shape='circle'
                  aria-label='Excel’den İçe Aktar'
                  icon={<UploadOutlined />}
                  onClick={() => { setImportResult(undefined); setImportOpen(true); }}
                />
              </Tooltip>
              <Tooltip title='Filtreler'>
                <Button
                  shape='circle'
                  aria-label='Filtreler'
                  icon={<Badge count={activeFilterCount} size='small'><FilterOutlined /></Badge>}
                  onClick={openFilterDrawer}
                />
              </Tooltip>
              <Tooltip title={`Toplu Sil (${selectedRowKeys.length})`}>
                <span>
                  <Button danger shape='circle' aria-label={`Toplu Sil (${selectedRowKeys.length})`}
                    icon={<DeleteOutlined />} disabled={!selectedRowKeys.length || bulkDeleting}
                    onClick={() => setBulkDeleteOpen(true)} />
                </span>
              </Tooltip>
              <Modal title='Soruları Toplu Sil' open={bulkDeleteOpen}
                onCancel={() => { if (!bulkDeletePending.current) setBulkDeleteOpen(false); }}
                onOk={handleBulkDelete} okText='Sil' cancelText='Vazgeç'
                okButtonProps={{ danger: true }} confirmLoading={bulkDeleting}
                cancelButtonProps={{ disabled: bulkDeleting }} closable={!bulkDeleting} maskClosable={!bulkDeleting} keyboard={!bulkDeleting}>
                Seçilen {selectedRowKeys.length} soru, alt soruları ve tanımlı cevap seçenekleriyle birlikte silinecek. Bu işlem geri alınamaz.
              </Modal>
              <Tooltip title={`Toplu Düzenle (${selectedRowKeys.length})`}>
                <span>
                  <Button
                    shape='circle'
                    aria-label={`Toplu Düzenle (${selectedRowKeys.length})`}
                    icon={<Badge count={selectedRowKeys.length} size='small'><EditOutlined /></Badge>}
                    disabled={!selectedRowKeys.length}
                    onClick={() => { void ensureOptionsLoaded(); setBulkOpen(true); }}
                  />
                </span>
              </Tooltip>
              <Tooltip title={bulkCloneDisabledReason ?? `Toplu Kopyala (${selectedRowKeys.length})`}>
                <span>
                  <Button
                    shape='circle'
                    aria-label={`Toplu Kopyala (${selectedRowKeys.length})`}
                    icon={<Badge count={selectedRowKeys.length} size='small'><CopyOutlined /></Badge>}
                    disabled={!selectedRowKeys.length || !!bulkCloneDisabledReason}
                    onClick={() => { bulkCloneForm.resetFields(); void ensureOptionsLoaded(); setBulkCloneOpen(true); }}
                  />
                </span>
              </Tooltip>
            </Space>
          )}
        >
          <Table
            rowKey='id'
            size='small'
            loading={loading}
            dataSource={questions}
            columns={columns}
            scroll={{ x: showReportStructure ? 1860 : 1600 }}
            rowSelection={{ selectedRowKeys, preserveSelectedRowKeys: true, onChange: handleSelectionChange }}
            pagination={false}
          />
        </Card>
      </Col>

      <Drawer title='Gelişmiş Soru Filtreleri' open={filterOpen} onClose={() => setFilterOpen(false)} width={760} destroyOnClose extra={<Space><Button icon={<ClearOutlined />} onClick={resetFilters}>Temizle</Button><Button type='primary' onClick={applyFilters}>Uygula</Button></Space>}>
        <Form form={filterForm} layout='vertical'>
          <Row gutter={16}>
            <Col xs={24} md={12}><Form.Item name='title' label='Başlık'><Input allowClear /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='description' label='Açıklama'><Input allowClear /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='questionType' label='Soru Tipi'><Select mode='multiple' allowClear options={QUESTION_TYPES.map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='reportStructureId' label='Rapor Yapısı'><TreeSelect allowClear showSearch treeDefaultExpandAll treeData={treeData} treeNodeFilterProp='title' placeholder='Seçiniz' /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='parentScope' label='Tablo İlişkisi'><Select allowClear options={[{ value: 'root', label: 'Ana soru' }, { value: 'child', label: 'Tablo alt sorusu' }]} /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='parentQuestionId' label='Üst Tablo Sorusu ID'><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='toolId' label='Bileşen'><Select allowClear showSearch optionFilterProp='label' options={toolOptions} /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name='controlAreas' label='Parametre Tipleri - Özellikleri'><Select mode='multiple' allowClear showSearch optionFilterProp='label' options={controlAreaOptions} /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item name='col' label='Sütun'><InputNumber min={1} max={24} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item name='titleCol' label='Başlık Sütunu'><InputNumber min={1} max={24} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item name='fixedRowCount' label='Sabit Satır'><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item name='digits' label='Ondalık Basamak'><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            {BOOLEAN_FILTERS.map(renderBooleanFilter)}
            {['tag', 'functionTag', 'functionText', 'checkReminderText', 'minValue', 'maxValue'].map((field) => <Col xs={24} md={12} key={field}><Form.Item name={field} label={BULK_FIELD_OPTIONS.find(([key]) => key === field)?.[1]}><Input allowClear /></Form.Item></Col>)}
            <Col xs={24} md={12}><Form.Item name='bulkInputMode' label='Veri Giriş Kapsamı'><Select allowClear options={[{ value: 'PER_REPORT', label: 'Kontrol Noktası Bazlı' }, { value: 'SHARED', label: 'Ortak Bilgi' }]} /></Form.Item></Col>
          </Row>
        </Form>
      </Drawer>

      <Drawer
        title='Excel’den Soru İçe Aktar'
        open={importOpen}
        onClose={() => setImportOpen(false)}
        width={760}
        extra={<Button type='primary' loading={importing} onClick={() => void importExcel()}>İçe Aktar</Button>}
      >
        <Typography.Paragraph>
          Yalnız ana sorular içe aktarılır. <code>TABLE</code> türü üst tablo sorusu olarak eklenebilir; tablo alt soruları oluşturulmaz.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Şablon, raporun parametresine bağlı aktif tip özelliklerini ayrı sütunlarda içerir. Çoktan seçmeli sorularda boş hücreler dahil her özellik için ayrı soru ve üç standart cevap oluşturulur.
          {' '}<code>U</code>: UYGUN varsayılan; <code>KD</code>: KAPSAM DIŞI varsayılan ve Formda Gözükmemeli açık; boş: varsayılan cevap yok. Diğer soru tiplerinde özellik hücreleri boş bırakılmalıdır.
        </Typography.Paragraph>
        <Typography.Paragraph type='secondary'>
          Dosyada <strong>Rapor Yapısı</strong>, Başlık, Soru Tipi, Sütun ve Başlık Sütunu zorunludur. Satırlardan biri geçersizse hiçbir soru eklenmez.
        </Typography.Paragraph>
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Button icon={<DownloadOutlined />} loading={downloadingTemplate} onClick={() => void downloadImportTemplate()}>Excel Şablonu İndir</Button>
          <label htmlFor='question-import-file'>.xlsx Dosyası</label>
          <input
            id='question-import-file'
            aria-label='Excel dosyası'
            type='file'
            accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            onChange={(event) => {
              setImportFile(event.target.files?.[0]);
              setImportResult(undefined);
            }}
          />
          {importFile ? <Typography.Text type='secondary'>{importFile.name}</Typography.Text> : null}
          {importResult?.success ? <Alert type='success' showIcon message={`${importResult.createdCount} soru başarıyla içe aktarıldı.`} /> : null}
          {importResult && !importResult.success ? <>
            <Alert type='error' showIcon message='Dosya doğrulanamadı; hiçbir soru eklenmedi.' description={`${importResult.totalRows} dolu satır incelendi.`} />
            <Table
              size='small'
              rowKey={(error) => `${error.rowNumber}-${error.column}-${error.message}`}
              pagination={false}
              dataSource={importResult.errors}
              columns={[
                { title: 'Satır', dataIndex: 'rowNumber', width: 90 },
                { title: 'Kolon', dataIndex: 'column', width: 180 },
                { title: 'Hata', dataIndex: 'message' },
              ]}
            />
          </> : null}
        </Space>
      </Drawer>

      <QuestionEditorDrawer
        open={!!editQuestion}
        questionId={editQuestion?.id === undefined ? undefined : Number(editQuestion.id)}
        parentQuestionId={editQuestion?.parentQuestionId}
        reportFormatId={reportFormatId}
        reportAttributeId={reportAttributeId}
        onClose={() => setEditQuestion(undefined)}
        onSuccess={() => {
          setEditQuestion(undefined);
          setRefreshKey((value) => value + 1);
        }}
      />

      <Drawer
        title={`Toplu Soru Düzenleme (${selectedRowKeys.length})`}
        open={bulkOpen}
        onClose={() => { setBulkSummaryOpen(false); setBulkOpen(false); }}
        width={760}
        destroyOnClose
        extra={<Button type='primary' onClick={() => void prepareBulkUpdate()}>Uygula</Button>}
      >
        <Typography.Paragraph type='secondary'>Yalnız seçtiğiniz ayarlar ve cevap işlemleri uygulanır; diğer bilgiler korunur.</Typography.Paragraph>
        <Form form={bulkForm} layout='vertical'>
          <Form.Item name='fields' label='Güncellenecek Ayarlar'>
            <Checkbox.Group options={BULK_FIELD_OPTIONS.map(([value, label]) => ({ value, label }))} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }} />
          </Form.Item>
          {selectedBulkFields.map(renderBulkControl)}
          {allSelectedQuestionsAreMultiple ? (
            <>
              <Divider />
              <Typography.Title level={5}>Cevap İşlemleri</Typography.Title>
              <Form.Item name='addStandardAnswers' valuePropName='checked' noStyle>
                <Checkbox style={{ display: 'none' }} aria-hidden='true' />
              </Form.Item>
              <Button
                type={addStandardAnswers ? 'primary' : 'default'}
                aria-pressed={addStandardAnswers}
                onClick={() => {
                  bulkForm.setFieldValue('addStandardAnswers', !addStandardAnswers);
                  bulkForm.setFields([{ name: 'fields', errors: [] }]);
                }}
              >
                Standart Cevap Ekle
              </Button>
              {addStandardAnswers ? (
                <Typography.Paragraph type='secondary' style={{ marginTop: 12 }}>UYGUN, UYGUN DEĞİL ve KAPSAM DIŞI cevapları eklenecek.</Typography.Paragraph>
              ) : null}
            </>
          ) : null}
        </Form>
      </Drawer>

      <Drawer
        title={`Soruları Toplu Kopyala (${selectedRowKeys.length})`}
        open={bulkCloneOpen}
        onClose={closeBulkCloneDrawer}
        width={640}
        destroyOnClose
        closable={!bulkCloning}
        maskClosable={!bulkCloning}
        keyboard={!bulkCloning}
        extra={<Button type='primary' loading={bulkCloning} onClick={() => void submitBulkClone()}>Kopyala</Button>}
      >
        <Typography.Paragraph type='secondary'>Her seçili soru, seçtiğiniz her Parametre Tipi - Özellik için ayrı kopyalanır. Her kopyada yalnız ilgili tip-özellik bulunur.</Typography.Paragraph>
        <Form form={bulkCloneForm} layout='vertical'>
          <Form.Item
            name='controlAreaIds'
            label='Parametre Tipleri - Özellikleri'
            rules={[{ required: true, type: 'array', min: 1, message: 'En az bir Parametre Tipi - Özellik seçiniz.' }]}
          >
            <Select mode='multiple' showSearch optionFilterProp='label' options={controlAreaOptions} />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={`Toplu İşlem Özeti (${selectedRowKeys.length})`}
        open={bulkSummaryOpen}
        onClose={() => setBulkSummaryOpen(false)}
        width={640}
        extra={(
          <Space>
            <Button disabled={saving} onClick={() => setBulkSummaryOpen(false)}>Geri Dön</Button>
            <Button type='primary' loading={saving} onClick={() => void submitBulkUpdate()}>Onayla ve Uygula</Button>
          </Space>
        )}
      >
        <Alert type='info' showIcon message={`${selectedRowKeys.length} soru bu toplu işlemden etkilenecek.`} />
        {pendingBulkPayload?.fields.length ? (
          <>
            <Typography.Title level={5} style={{ marginTop: 24 }}>Ayar Değişiklikleri</Typography.Title>
            <Descriptions
              bordered
              size='small'
              column={1}
              items={pendingBulkPayload.fields.map((field) => ({
                key: field,
                label: BULK_FIELD_OPTIONS.find(([key]) => key === field)?.[1] ?? field,
                children: bulkValueLabel(field, (pendingBulkPayload as any)[field]),
              }))}
            />
          </>
        ) : null}
        {pendingBulkPayload?.addStandardAnswers ? (
          <>
            <Typography.Title level={5} style={{ marginTop: 24 }}>Cevap İşlemleri</Typography.Title>
            <Descriptions
              bordered
              size='small'
              column={1}
              items={[{
                key: 'standardAnswers',
                label: 'Standart Cevap Ekle',
                children: 'UYGUN, UYGUN DEĞİL ve KAPSAM DIŞI',
              }]}
            />
          </>
        ) : null}
      </Drawer>
    </Row>
  );
}
